import { TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { ACCOUNT_NOTIFICATION_SETTINGS } from "../../constants/routes";
import { useEvents } from "../../hooks";
import { useAnnouncementList } from "../../hooks/api/v2/announcement/useAnnouncementList";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useSearchParamState } from "../../hooks/useSearchParamState";
import { Button, Icon } from "@sellar-io/components";
import { NavigationTab } from "../navigationTabs/NavigationTab";

type TabType = "Activity" | "News";
export const NotificationTabs = ({
  tab1,
  tab2,
  isNotificationPopover,
  showSettings,
  mixpanelEventName,
}: {
  isNotificationPopover: boolean;
  showSettings: boolean;
  mixpanelEventName?: string;
  tab1?: React.ReactNode;
  tab2?: React.ReactNode;
}) => {
  const { track } = useEvents();
  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useSearchParamState<TabType>(
    "tab",
    "Activity",
  );
  const tabs: TabType[] = ["Activity", "News"];
  const tabAsIndex = (tab: TabType) => tabs.indexOf(tab);

  const indexAsTab = (index: number) => tabs[index] ?? "Activity";

  // Ensure the selectedTab is valid, fallback to "Activity" if not
  const validSelectedTab = tabs.includes(selectedTab)
    ? selectedTab
    : "Activity";

  // Check for unread announcement
  const unreadAnnouncementQuery = useAnnouncementList(
    {
      query: [{ type: "unRead", value: true, modifier: "EQUALS" }],
      limit: 1,
    },
    {
      enabled: !!business?.Retailer,
    },
  );

  const hasUnreadAnnouncement =
    (unreadAnnouncementQuery.data?.pages[0]?.length || 0) > 0;

  useEffect(() => {
    if (selectedTab && mixpanelEventName) {
      track(mixpanelEventName, {
        tab: selectedTab,
      });
    }
  }, [selectedTab, track, mixpanelEventName]);
  return (
    <>
      <TabGroup
        className={"flex w-full flex-col gap-2"}
        selectedIndex={tabAsIndex(validSelectedTab)}
        onChange={(index) => setSelectedTab(indexAsTab(index))}
      >
        <div className="flex w-full flex-row items-center justify-between border-b">
          <TabList
            className={twMerge(
              "transparent flex w-1/2",
              isNotificationPopover && "w-full",
            )}
          >
            {(business?.Retailer || !isNotificationPopover) && (
              <NavigationTab
                className={twMerge(!isNotificationPopover && "bg-transparent")}
              >
                Activity
              </NavigationTab>
            )}
            {business?.Retailer && (
              <>
                <NavigationTab
                  className={twMerge(
                    !isNotificationPopover && "bg-transparent",
                  )}
                  showRedDot={hasUnreadAnnouncement}
                >
                  News
                </NavigationTab>
              </>
            )}
          </TabList>
          {showSettings && (
            <Button
              color={"transparent"}
              className={"p-0 pt-2 pb-2 text-zinc-800"}
              onClick={() =>
                navigate(ACCOUNT_NOTIFICATION_SETTINGS, {
                  state: {
                    source: "Notification page",
                  },
                })
              }
            >
              Settings <Icon icon={"COG"} className={"text-zinc-800"} />
            </Button>
          )}
        </div>
        <TabPanels>
          <TabPanel>{tab1}</TabPanel>
          <TabPanel>{tab2}</TabPanel>
        </TabPanels>
      </TabGroup>
    </>
  );
};
